import { ChronoUnit, DayOfWeek, LocalDate, LocalTime } from "@js-joda/core";
import assertNever from "assert-never";
import { match } from "ts-pattern";
import {
  FlexibleVisitDayCalculatedState,
  FlexibleVisitDaysMap,
  FlexibleVisitDayState,
  FlexibleVisitDuration,
} from "../components/useFlexibleVisit";
import {
  CaregiverFlexibleVisitRequestParams,
  CaregiverVisitBroadcastEngagementDetails,
  FlexibleVisitParams,
  PatientTimeFrames,
  PublicOnboardingVisitDetails,
  PublicVisitBroadcastDetails,
  PublicVisitBroadcastDetailsTypes,
  VisitBroadcastId,
} from "../messages";
import { assertDefined } from "./assertUndefinedable";
import { HashMap } from "./HashMap";

export function getAreDaysSelectable(
  visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern
): boolean {
  return match({ type: visit.flexibleVisitParams.type, multiple: visit.allowMultipleCaregivers })
    .with({ type: "PermanentFlexibleHoursAndDaysPerWeek", multiple: true }, () => true)
    .with({ type: "PermanentFlexibleHoursAndDaysPerWeek", multiple: false }, () => true)
    .with({ type: "PermanentFlexibleHoursAndDaysPattern", multiple: true }, () => true)
    .with({ type: "PermanentFlexibleHoursAndDaysPattern", multiple: false }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPattern", multiple: true }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPattern", multiple: false }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPerWeek", multiple: true }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPerWeek", multiple: false }, () => false)
    .with({ type: "PermanentFixedHoursFlexibleDays", multiple: true }, () => true)
    .with({ type: "PermanentFixedHoursFlexibleDays", multiple: false }, () => true)
    .with({ type: "SingleFlexible" }, () => false)
    .exhaustive();
}

export function canSelectAllDays(visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern) {
  if (
    getFlexibleVisitDaysLength(visit.flexibleVisitParams) <= 1 &&
    (visit.flexibleVisitParams.type === "PermanentFlexibleHoursFixedDaysPattern" ||
      visit.flexibleVisitParams.type === "PermanentFlexibleHoursFixedDaysPerWeek")
  ) {
    return false;
  }

  return match({ type: visit.flexibleVisitParams.type, multiple: visit.allowMultipleCaregivers })
    .with({ type: "PermanentFlexibleHoursAndDaysPerWeek", multiple: false }, () => false)
    .with({ type: "PermanentFlexibleHoursAndDaysPerWeek", multiple: true }, () => false)
    .with({ type: "PermanentFlexibleHoursAndDaysPattern", multiple: false }, () => true)
    .with({ type: "PermanentFlexibleHoursAndDaysPattern", multiple: true }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPattern", multiple: false }, () => false)
    .with({ type: "PermanentFlexibleHoursFixedDaysPattern", multiple: true }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPerWeek", multiple: false }, () => false)
    .with({ type: "PermanentFlexibleHoursFixedDaysPerWeek", multiple: true }, () => false)
    .with({ type: "PermanentFixedHoursFlexibleDays", multiple: false }, () => true)
    .with({ type: "PermanentFixedHoursFlexibleDays", multiple: true }, () => true)
    .with({ type: "SingleFlexible" }, () => false)
    .exhaustive();
}

export function getAreTimesFlexible(visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern) {
  return match(visit.flexibleVisitParams)
    .with({ type: "PermanentFlexibleHoursAndDaysPerWeek" }, () => true)
    .with({ type: "PermanentFlexibleHoursAndDaysPattern" }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPattern" }, () => true)
    .with({ type: "PermanentFlexibleHoursFixedDaysPerWeek" }, () => true)
    .with({ type: "PermanentFixedHoursFlexibleDays" }, () => false)
    .with({ type: "SingleFlexible" }, () => true)
    .exhaustive();
}

export function getHoursInWeek(visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern) {
  switch (visit.flexibleVisitParams.type) {
    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
      return visit.flexibleVisitParams.amountOfHoursPerWeek;

    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "PermanentFixedHoursFlexibleDays":
    case "SingleFlexible":
      return null;

    default:
      assertNever(visit.flexibleVisitParams, true);
  }
}

export function getDaysInWeek(visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern) {
  switch (visit.flexibleVisitParams.type) {
    case "PermanentFixedHoursFlexibleDays": {
      if (!visit.allowMultipleCaregivers) return visit.flexibleVisitParams.amountOfDays;

      const includedDays = visit.flexibleVisitParams.daysToChooseFrom.filter(
        (day) => !visit.flexibleVisitParams.excludedDaysFromCaregivers.includes(day.day)
      );

      return Math.min(includedDays.length, visit.flexibleVisitParams.amountOfDays);
    }

    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "SingleFlexible":
      return null;

    default:
      assertNever(visit.flexibleVisitParams, true);
  }
}

export function getHoursPerDayMap(
  visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern
): HashMap<number, number> {
  switch (visit.flexibleVisitParams.type) {
    case "PermanentFlexibleHoursAndDaysPattern":
      return HashMap.fromArray(
        visit.flexibleVisitParams.flexibleDetails.map((flexibleDetail) => [
          flexibleDetail.amountOfHours,
          flexibleDetail.amountOfDays,
        ])
      );

    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "PermanentFixedHoursFlexibleDays":
    case "SingleFlexible":
      return HashMap.empty();

    default:
      assertNever(visit.flexibleVisitParams, true);
  }
}

export function isFlexibleVisitValid(
  visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern,
  calculatedDays: FlexibleVisitDayCalculatedState[],
  daysMapValues: FlexibleVisitDayState[],
  daysMap: FlexibleVisitDaysMap,
  hoursInWeekLeft: number | null,
  daysInWeekLeft: number | null
) {
  const availableTimeRanges = getAvailableTimeRanges(daysMap);

  if (!getIsValidShiftsTimeRange(daysMap, availableTimeRanges)) {
    return false;
  }

  if (
    calculatedDays
      .filter((day) => day.isDayChecked)
      .some((day) => !day.isValidRange || !day.isSelectedTimeInAvailabilityRange)
  ) {
    return false;
  }

  const hasSelectedAtLeastOneShift = daysMapValues.some(
    (day) => day.isChecked === true && day.durations.some((duration) => duration.isSelected)
  );

  const isSelectedHoursInPatientAvailability =
    getIsSelectedDaysTimesInAvailabilityRange(daysMapValues);

  const hasSelectedAtLeastOneDay = daysMapValues.some((day) => day.isChecked);

  switch (visit.flexibleVisitParams.type) {
    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
      return (
        (visit.allowMultipleCaregivers ? hasSelectedAtLeastOneShift : hoursInWeekLeft === 0) &&
        isSelectedHoursInPatientAvailability
      );

    case "PermanentFlexibleHoursAndDaysPattern": {
      if (visit.allowMultipleCaregivers) {
        return (
          daysMapValues.some((day) => day.durations.some((duration) => duration.isSelected)) &&
          isSelectedHoursInPatientAvailability
        );
      }

      const requiredDaysNumber = visit.flexibleVisitParams.flexibleDetails.reduce(
        (acc, flexibleDetail) => acc + flexibleDetail.amountOfDays,
        0
      );

      return (
        daysMapValues.filter((day) => day.durations.some((duration) => duration.isSelected))
          .length === requiredDaysNumber && isSelectedHoursInPatientAvailability
      );
    }
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "SingleFlexible":
      return (
        (visit.allowMultipleCaregivers
          ? hasSelectedAtLeastOneShift
          : !daysMapValues.some((day) => day.durations.some((duration) => !duration.isSelected))) &&
        isSelectedHoursInPatientAvailability
      );
    case "PermanentFixedHoursFlexibleDays":
      return visit.allowMultipleCaregivers ? hasSelectedAtLeastOneDay : daysInWeekLeft === 0;

    default:
      assertNever(visit.flexibleVisitParams);
  }
}

function getStartTime(
  day: DayOfWeek,
  startDate: LocalDate,
  endDate: LocalDate,
  flexibleTimeRequest: CaregiverFlexibleVisitRequestParams[]
) {
  const startsToday =
    (startDate.equals(LocalDate.now()) || startDate.isBefore(LocalDate.now())) &&
    day.equals(LocalDate.now().dayOfWeek());

  const mustChooseTimeslotInFuture = startsToday && endDate.isBefore(LocalDate.now().plusWeeks(1));

  const minStartTime = mustChooseTimeslotInFuture
    ? LocalTime.now().isAfter(LocalTime.of(23, 0))
      ? LocalTime.of(23, 0)
      : addHoursAndRoundUp({ time: LocalTime.now(), hoursToAdd: 1, roundingMinutes: 30 })
    : LocalTime.of(0, 0);

  const defaultStartTime =
    flexibleTimeRequest.find((engagedDay) => engagedDay.day === day)?.startTime ??
    LocalTime.parse("09:00");

  return minStartTime.isAfter(defaultStartTime) ? minStartTime : defaultStartTime;
}

function getDurationsForPerWeekVisits(maxHoursPerShift: number, minHoursPerShift: number) {
  return Array.from({ length: maxHoursPerShift - minHoursPerShift + 1 }, (_, k) => ({
    numberOfHours: k + minHoursPerShift,
    isDisabled: false,
    isSelected: false,
  }));
}

export function getDaysMap(visit: {
  type: "Available";
  id: VisitBroadcastId;
  publicVisitBroadcastDetails: PublicVisitBroadcastDetailsTypes.FlexiblePattern;
  caregiverVisitEngagementDetails: CaregiverVisitBroadcastEngagementDetails;
}): FlexibleVisitDaysMap {
  const flexible = visit.publicVisitBroadcastDetails;
  const startDate = visit.publicVisitBroadcastDetails.startTimeLocal.toLocalDate();
  const endDate = visit.publicVisitBroadcastDetails.endTimeLocal.toLocalDate();
  const getEngagementByDay = (day: DayOfWeek) => {
    return visit.caregiverVisitEngagementDetails.flexibleVisitTimeRequest?.find(
      (engagedDay) => engagedDay.day === day
    );
  };

  const isChecked = (day: DayOfWeek) => {
    return getEngagementByDay(day) !== undefined;
  };

  switch (flexible.flexibleVisitParams.type) {
    case "PermanentFlexibleHoursAndDaysPerWeek": {
      const { minHoursPerShift, maxHoursPerShift } = flexible.flexibleVisitParams;
      return HashMap.fromArray(
        flexible.flexibleVisitParams.daysToChooseFrom.map((day) => [
          day,
          {
            isChecked: isChecked(day),
            isExcluded: flexible.flexibleVisitParams.excludedDaysFromCaregivers.includes(day),
            isDisabled: false,
            amountOfHours: 0,
            durations: getDurationsForPerWeekVisits(maxHoursPerShift, minHoursPerShift),
            startTime: getStartTime(
              day,
              startDate,
              endDate,
              visit.caregiverVisitEngagementDetails.flexibleVisitTimeRequest ?? []
            ),
            isTimeFlexible: true,
            timeframes:
              flexible.patientAvailability
                ?.filter((pa) => pa.day.name() === day.toString())
                .flatMap((pa) => pa.timeframes) ?? [],
          },
        ])
      );
    }

    case "PermanentFlexibleHoursAndDaysPattern": {
      const hours = flexible.flexibleVisitParams.flexibleDetails.map(
        (flexibleDetail) => flexibleDetail.amountOfHours
      );

      return HashMap.fromArray(
        flexible.flexibleVisitParams.daysToChooseFrom.map((day) => [
          day,
          {
            isChecked: isChecked(day),
            isExcluded: flexible.flexibleVisitParams.excludedDaysFromCaregivers.includes(day),
            isDisabled: false,
            amountOfHours: 0,
            durations: hours.map((hour) => ({
              numberOfHours: hour,
              isDisabled: false,
              isSelected: false,
            })),
            startTime: getStartTime(
              day,
              startDate,
              endDate,
              visit.caregiverVisitEngagementDetails.flexibleVisitTimeRequest ?? []
            ),
            isTimeFlexible: true,
            timeframes:
              flexible.patientAvailability
                ?.filter((pa) => pa.day.name() === day.toString())
                .flatMap((pa) => pa.timeframes) ?? [],
          },
        ])
      );
    }

    case "PermanentFlexibleHoursFixedDaysPattern":
      return HashMap.fromArray(
        flexible.flexibleVisitParams.details.map((data) => [
          data.day,
          {
            isChecked: flexible.allowMultipleCaregivers ? isChecked(data.day) : null,
            isExcluded: flexible.flexibleVisitParams.excludedDaysFromCaregivers.includes(data.day),
            isDisabled: false,
            durations: [
              {
                numberOfHours: data.amountOfHours,
                isDisabled: false,
                isSelected: true,
              },
            ],
            startTime: getStartTime(
              data.day,
              startDate,
              endDate,
              visit.caregiverVisitEngagementDetails.flexibleVisitTimeRequest ?? []
            ),
            isTimeFlexible: !flexible.allowMultipleCaregivers,
            timeframes:
              flexible.patientAvailability
                ?.filter((pa) => pa.day.name() === data.day.toString())
                .flatMap((pa) => pa.timeframes) ?? [],
          },
        ])
      );

    case "PermanentFlexibleHoursFixedDaysPerWeek": {
      const { minHoursPerShift, maxHoursPerShift } = flexible.flexibleVisitParams;

      return HashMap.fromArray(
        flexible.flexibleVisitParams.days.map((day) => [
          day,
          {
            isChecked: flexible.allowMultipleCaregivers ? isChecked(day) : null,
            isExcluded: flexible.flexibleVisitParams.excludedDaysFromCaregivers.includes(day),
            isDisabled: false,
            amountOfHours: 0,
            durations: getDurationsForPerWeekVisits(maxHoursPerShift, minHoursPerShift),
            startTime: getStartTime(
              day,
              startDate,
              endDate,
              visit.caregiverVisitEngagementDetails.flexibleVisitTimeRequest ?? []
            ),
            isTimeFlexible: true,
            timeframes:
              flexible.patientAvailability
                ?.filter((pa) => pa.day.name() === day.toString())
                .flatMap((pa) => pa.timeframes) ?? [],
          },
        ])
      );
    }

    case "PermanentFixedHoursFlexibleDays":
      return HashMap.fromArray(
        flexible.flexibleVisitParams.daysToChooseFrom.map((data) => [
          data.day,
          {
            isChecked: isChecked(data.day),
            isExcluded: flexible.flexibleVisitParams.excludedDaysFromCaregivers.includes(data.day),
            isDisabled: false,
            amountOfHours: 0,
            durations: [
              {
                numberOfHours: data.startTime.until(data.endTime, ChronoUnit.MINUTES) / 60,
                isDisabled: false,
                isSelected: false,
              },
            ],
            endTime: getEngagementByDay(data.day)?.endTime ?? data.endTime,
            startTime: getEngagementByDay(data.day)?.startTime ?? data.startTime,
            isTimeFlexible: false,
            timeframes: [],
          },
        ])
      );

    case "SingleFlexible": {
      const dayOfWeek = flexible.flexibleVisitParams.date.dayOfWeek();
      const duration = flexible.flexibleVisitParams.amountOfHours;

      return HashMap.fromArray([
        [
          dayOfWeek,
          {
            isChecked: true,
            isExcluded: false,
            isDisabled: false,
            amountOfHours: flexible.flexibleVisitParams.amountOfHours,
            durations: [
              {
                numberOfHours: duration,
                isDisabled: false,
                isSelected: true,
              },
            ],
            startTime: getStartTime(
              dayOfWeek,
              startDate,
              endDate,
              visit.caregiverVisitEngagementDetails.flexibleVisitTimeRequest ?? []
            ),
            isTimeFlexible: false,
            timeframes:
              flexible.patientAvailability
                ?.filter((pa) => pa.day.name() === dayOfWeek.toString())
                .flatMap((pa) => pa.timeframes) ?? [],
          },
        ],
      ]);
    }

    default:
      assertNever(flexible.flexibleVisitParams, true);
  }
}

export function calculateHoursLeftInWeek(
  visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern,
  newData: FlexibleVisitDaysMap
) {
  switch (visit.flexibleVisitParams.type) {
    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
      return (
        (getHoursInWeek(visit) ?? 0) -
        [...newData.values()]
          .filter((data) => data.isChecked !== false)
          .reduce(
            (acc, cur) =>
              acc + (cur.durations.find((duration) => duration.isSelected)?.numberOfHours ?? 0),
            0
          )
      );

    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "PermanentFixedHoursFlexibleDays":
    case "SingleFlexible":
      return null;

    default:
      assertNever(visit.flexibleVisitParams, true);
  }
}

export function calculateDaysLeftInWeek(
  visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern,
  newData: FlexibleVisitDaysMap
) {
  switch (visit.flexibleVisitParams.type) {
    case "PermanentFixedHoursFlexibleDays": {
      const selectedDays = [...newData.values()].filter((data) => data.isChecked !== false);

      if (!visit.allowMultipleCaregivers) {
        return visit.flexibleVisitParams.amountOfDays - selectedDays.length;
      }

      const includedDays = visit.flexibleVisitParams.daysToChooseFrom.filter(
        (day) => !visit.flexibleVisitParams.excludedDaysFromCaregivers.includes(day.day)
      );

      return (
        Math.min(includedDays.length, visit.flexibleVisitParams.amountOfDays) - selectedDays.length
      );
    }

    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFlexibleHoursFixedDaysPattern":
    case "SingleFlexible":
      return null;

    default:
      assertNever(visit.flexibleVisitParams, true);
  }
}

export function localTimeToMinutes(time: LocalTime) {
  return time.toSecondOfDay() / 60;
}

export function getStartAndEndTimeMinutes(value: number[], duration: number) {
  let [newStartTimeMinutes, newEndTimeMinutes] = value;

  newStartTimeMinutes = Math.min(60 * 24 - duration * 60, newStartTimeMinutes);
  newEndTimeMinutes = Math.max(duration * 60, newEndTimeMinutes);

  return { newStartTimeMinutes, newEndTimeMinutes };
}

export function fixStartTimeOnDurationChange(startTime: LocalTime, duration: number) {
  if (duration === 24) {
    return startTime;
  }

  const startTimeMinutes = localTimeToMinutes(startTime);

  const diff = startTimeMinutes + duration * 60 - 60 * 24;

  if (diff > 0) {
    return startTime.minusMinutes(diff);
  }

  return startTime;
}

export function isDurationDisabled(
  duration: FlexibleVisitDuration,
  durations: FlexibleVisitDuration[],
  daysMap: FlexibleVisitDaysMap,
  hoursInWeekLeft: number | null,
  daysInWeekLeft: number | null,
  hoursPerDayMap: HashMap<number, number>,
  areDaysSelectable: boolean,
  isMultipleCaregivers: boolean
) {
  return (
    !canSelectDuration(
      duration,
      durations,
      daysMap,
      hoursInWeekLeft,
      daysInWeekLeft,
      hoursPerDayMap,
      areDaysSelectable,
      isMultipleCaregivers
    ) && !duration.isSelected
  );
}

export function canSelectDuration(
  duration: FlexibleVisitDuration,
  durations: FlexibleVisitDuration[],
  daysMap: FlexibleVisitDaysMap,
  hoursInWeekLeft: number | null,
  daysInWeekLeft: number | null,
  hoursPerDayMap: HashMap<number, number>,
  areDaysSelectable: boolean,
  isMultipleCaregivers: boolean
) {
  if (hoursInWeekLeft === null) {
    if (daysInWeekLeft === null) {
      return canSelectDurationHoursPerDay(duration, daysMap, hoursPerDayMap);
    }

    return true;
  }

  return canSelectDurationWeeklyHours(
    duration,
    durations,
    daysMap,
    hoursInWeekLeft,
    areDaysSelectable,
    isMultipleCaregivers
  );
}

export function getIsDayDisabled(
  data: FlexibleVisitDaysMap,
  entry: FlexibleVisitDayState,
  day: DayOfWeek,
  hoursInWeekLeft: number | null,
  daysInWeekLeft: number | null,
  areDaysSelectable: boolean,
  hoursPerDayMap: HashMap<number, number>,
  isMultipleCaregivers: boolean
) {
  if (entry.isTimeFlexible) {
    if (
      !entry.durations.some(
        (d) =>
          !isDurationDisabled(
            d,
            entry.durations,
            data,
            hoursInWeekLeft,
            daysInWeekLeft,
            hoursPerDayMap,
            areDaysSelectable,
            isMultipleCaregivers
          )
      )
    ) {
      return true;
    }
  }

  if (daysInWeekLeft !== null && daysInWeekLeft === 0) {
    return ![...data.entries()].some(([dayInWeek, e]) => e.isChecked === true && dayInWeek === day);
  }

  return false;
}

export function canSelectDurationHoursPerDay(
  duration: FlexibleVisitDuration,
  daysMap: FlexibleVisitDaysMap,
  hoursPerDayMap: HashMap<number, number>
) {
  const maxAllowedHours = hoursPerDayMap.get(duration.numberOfHours);

  if (maxAllowedHours === undefined) {
    return false;
  }

  const selectedHours = [...daysMap.values()].flatMap((data) => {
    if (data.isChecked === false) {
      return [];
    }

    const selectedDuration = data.durations.find((d) => d.isSelected);

    if (selectedDuration === undefined) {
      return [];
    }

    return [selectedDuration.numberOfHours];
  });

  const selectedHoursCountMap = aggregateArrayAndCount(selectedHours);

  const selectedHourCount = selectedHoursCountMap.get(duration.numberOfHours);

  return selectedHourCount === undefined || selectedHourCount < maxAllowedHours;
}

export function canSelectDurationWeeklyHours(
  duration: FlexibleVisitDuration,
  durations: FlexibleVisitDuration[],
  daysMap: FlexibleVisitDaysMap,
  hoursInWeekLeft: number,
  areDaysSelectable: boolean,
  isMultipleCaregivers: boolean
) {
  const selectedDuration = durations.find((d) => d.isSelected);

  if (selectedDuration !== undefined) {
    return (
      selectedDuration.numberOfHours < hoursInWeekLeft || duration.numberOfHours <= hoursInWeekLeft
    );
  }

  if (duration.numberOfHours > hoursInWeekLeft) {
    return false;
  }

  if (isMultipleCaregivers) {
    return true;
  }

  const freeDays = [...daysMap.values()].filter(
    (data) => !data.durations.some((d) => d.isSelected) && !data.isExcluded
  );

  const numbers = freeDays.flatMap((data) => data.durations.map((d) => d.numberOfHours));

  const availableHours = uniqueSubsetSumWithCache(numbers, hoursInWeekLeft).filter((sets) =>
    areDaysSelectable ? sets.length <= freeDays.length : sets.length === freeDays.length
  );

  return availableHours.some((hours) => hours.includes(duration.numberOfHours));
}

const cachedUniqueSubsetSum = new Map<string, number[][]>();

function uniqueSubsetSumWithCache(numbers: number[], target: number) {
  const key = JSON.stringify({ numbers, target });
  const cached = cachedUniqueSubsetSum.get(key);

  if (cached !== undefined) {
    return cached;
  }

  const value = uniqueSubsetSum(numbers, target);
  cachedUniqueSubsetSum.set(key, value);

  return value;
}

export function uniqueSubsetSum(
  numbers: number[],
  target: number,
  partial: number[] = [],
  result: Map<string, number[]> = new Map()
) {
  // sum partial
  const partialSum = partial.reduce((a, b) => a + b, 0);

  // check if the partial sum is equals to target
  if (partialSum === target) {
    result.set([...partial.sort((a, b) => a - b)].join("_"), partial);
  }

  if (partialSum >= target) {
    return [...result.values()]; // if we reach the number why bother to continue
  }

  for (let i = 0; i < numbers.length; i++) {
    const num = numbers[i];
    const remaining = numbers.slice(i + 1);
    uniqueSubsetSum(remaining, target, partial.concat([num]), result);
  }

  return [...result.values()];
}

export function aggregateArrayAndCount(data: number[]) {
  const aggregator = new Map<number, number>();

  for (const item of data) {
    const count = aggregator.get(item) ?? 0;
    aggregator.set(item, count + 1);
  }

  return aggregator;
}

export function isOvernightShift(startTime: LocalTime, endTime: LocalTime) {
  return startTime.isAfter(endTime) || startTime.equals(endTime);
}

export function getTotalFlexibleVisitHours(flexibleVisitParams: FlexibleVisitParams) {
  let totalHours = 0;

  switch (flexibleVisitParams.type) {
    case "PermanentFixedHoursFlexibleDays": {
      const hours = [];

      for (const day of flexibleVisitParams.daysToChooseFrom) {
        const startTime = day.startTime.hour();
        let endTime = day.endTime.hour();

        // In case of an overnight shift, the end hours will be before the start hour (e.g. 23:00 - 02:00)
        // In this case, we add 24 hours to the end time to make it a valid duration
        if (isOvernightShift(day.startTime, day.endTime)) {
          endTime += 24;
        }
        hours.push(endTime - startTime);
      }

      hours.sort((a, b) => b - a);

      for (const hour of hours.slice(0, flexibleVisitParams.amountOfDays)) {
        totalHours += hour;
      }

      return totalHours;
    }
    case "PermanentFlexibleHoursAndDaysPattern": {
      for (const day of flexibleVisitParams.flexibleDetails) {
        totalHours += day.amountOfHours * day.amountOfDays;
      }

      return totalHours;
    }

    case "PermanentFlexibleHoursFixedDaysPattern": {
      for (const day of flexibleVisitParams.details) {
        totalHours += day.amountOfHours;
      }

      return totalHours;
    }

    case "SingleFlexible":
      return flexibleVisitParams.amountOfHours;

    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
      return flexibleVisitParams.amountOfHoursPerWeek;

    default:
      assertNever(flexibleVisitParams);
  }
}

export function getFlexibleVisitDaysLength(flexibleVisitParams: FlexibleVisitParams) {
  switch (flexibleVisitParams.type) {
    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursAndDaysPattern":
    case "PermanentFixedHoursFlexibleDays":
      return flexibleVisitParams.daysToChooseFrom.length;

    case "PermanentFlexibleHoursFixedDaysPattern":
      return flexibleVisitParams.details.length;

    case "PermanentFlexibleHoursFixedDaysPerWeek":
      return flexibleVisitParams.days.length;

    case "SingleFlexible":
      return 1;

    default:
      assertNever(flexibleVisitParams, true);
  }
}

export function getIsSelectedDaysTimesInAvailabilityRange(daysMapValues: FlexibleVisitDayState[]) {
  return daysMapValues
    .filter((day) => day.isChecked === true && day.durations.some((d) => d.isSelected))
    .every(getIsSelectedTimeInAvailabilityRange);
}

export function getIsSelectedTimeInAvailabilityRange(day: FlexibleVisitDayState): boolean {
  return (
    day.timeframes.some((tf) => isDaySelectedHoursInPatientTimeframes(day, tf)) ||
    day.timeframes.length === 0
  );
}

function isDaySelectedHoursInPatientTimeframes(
  day: FlexibleVisitDayState,
  timeFrames: PatientTimeFrames
): boolean {
  let frameStartTime = LocalTime.of(timeFrames.startTime, 0, 0);
  const frameEndTime = LocalTime.of(timeFrames.endTime, 0, 0);

  if (timeFrames.startTime > timeFrames.endTime) {
    frameStartTime = LocalTime.of(0, 0, 0);
  }

  return !frameStartTime.isAfter(day.startTime) && !frameEndTime.isBefore(day.startTime);
}

export function getAvailableTimeRangeForDay(params: {
  daysMap: FlexibleVisitDaysMap;
  day: DayOfWeek;
}) {
  const previousDay = params.daysMap.get(params.day.minus(1));
  const nextDay = params.daysMap.get(params.day.plus(1));

  const previousDayShift =
    previousDay !== undefined && previousDay.isChecked === true ? previousDay : null;

  const nextDayShift = nextDay !== undefined && nextDay.isChecked === true ? nextDay : null;

  const from =
    previousDayShift !== null &&
    previousDayShift.endTime !== undefined &&
    isOvernightShift(previousDayShift.startTime, previousDayShift.endTime)
      ? previousDayShift.endTime
      : LocalTime.of(0, 0, 0);

  const to = nextDayShift !== null ? nextDayShift.startTime : null;

  return { from, to };
}

export function getIsValidShiftTimeRange(
  shift: FlexibleVisitDayState,
  availableHours: { from: LocalTime; to: LocalTime | null }
) {
  if (shift.isChecked === true) {
    if (shift.startTime.isBefore(availableHours.from)) {
      return false;
    }

    if (
      shift.endTime !== undefined &&
      isOvernightShift(shift.startTime, shift.endTime) &&
      availableHours.to !== null &&
      shift.endTime.isAfter(availableHours.to)
    ) {
      return false;
    }
  }

  return true;
}

export function getAvailableTimeRanges(daysMap: FlexibleVisitDaysMap) {
  const availableTimeRanges = new Map<DayOfWeek, { from: LocalTime; to: LocalTime | null }>();

  for (const day of daysMap.keys()) {
    availableTimeRanges.set(day, getAvailableTimeRangeForDay({ daysMap, day }));
  }

  return availableTimeRanges;
}

export function getIsValidShiftsTimeRange(
  daysMap: FlexibleVisitDaysMap,
  availableHoursMap: Map<
    DayOfWeek,
    {
      from: LocalTime;
      to: LocalTime | null;
    }
  >
) {
  return [...daysMap.entries()].every(([day, shift]) =>
    getIsValidShiftTimeRange(shift, assertDefined(availableHoursMap.get(day)))
  );
}

export function getDisabledFlexibleDays(daysMap: FlexibleVisitDaysMap) {
  return Array.from(daysMap.entries())
    .filter(([, data]) => data.isChecked !== true)
    .map(([day]) => day);
}

export function updateFlexibleDaysTimes({
  startTime,
  endTime,
  daysToUpdate,
  daysMap,
}: {
  startTime: LocalTime;
  endTime: LocalTime;
  daysToUpdate: DayOfWeek[];
  daysMap: FlexibleVisitDaysMap;
}) {
  let newDaysMap = daysMap;

  for (const dayToUpdate of daysToUpdate) {
    newDaysMap = newDaysMap.set(dayToUpdate, {
      ...assertDefined(newDaysMap.get(dayToUpdate)),
      startTime,
      endTime,
    });
  }

  return newDaysMap;
}

export function getFlexibleVisitDayCalculatedState(params: {
  visit: PublicVisitBroadcastDetailsTypes.FlexiblePattern;
  daysMap: FlexibleVisitDaysMap;
  day: DayOfWeek;
  state: FlexibleVisitDayState;
  areTimesFlexible: boolean;
}): FlexibleVisitDayCalculatedState {
  const day = params.day;

  const { isChecked, startTime, durations, endTime } = params.state;

  const startDate = params.visit.startTimeLocal.toLocalDate();
  const endDate = params.visit.endTimeLocal.toLocalDate();

  const areTimesFlexible = params.areTimesFlexible;

  const isSelectedTimeInAvailabilityRange = getIsSelectedTimeInAvailabilityRange(params.state);

  const durationInHours = durations.find((duration) => duration.isSelected)?.numberOfHours ?? null;

  const firstDuration = durations[0]?.numberOfHours ?? 4;

  const knownDurationInHours = durations.length === 1 ? firstDuration : durationInHours;

  const isOvernight = endTime !== undefined && isOvernightShift(startTime, endTime);

  const isDayChecked = isChecked === true || (isChecked === null && durations.length === 1);

  const isDurationSelected = isDayChecked && durations.some((duration) => duration.isSelected);

  const showTimesLabel = areTimesFlexible && knownDurationInHours !== null && isDurationSelected;

  const startsToday =
    (startDate.equals(LocalDate.now()) || startDate.isBefore(LocalDate.now())) &&
    day.equals(LocalDate.now().dayOfWeek());

  const mustChooseTimeslotInFuture = startsToday && endDate.isBefore(LocalDate.now().plusWeeks(1));

  const minStartTime = mustChooseTimeslotInFuture
    ? LocalTime.now().isAfter(LocalTime.of(23, 0))
      ? LocalTime.of(23, 0)
      : addHoursAndRoundUp({ time: LocalTime.now(), hoursToAdd: 1, roundingMinutes: 30 })
    : LocalTime.of(0, 0);

  const availableTimeRange = getAvailableTimeRangeForDay({
    day: day,
    daysMap: params.daysMap,
  });

  const isValidRange = getIsValidShiftTimeRange(params.state, availableTimeRange);

  const state: FlexibleVisitDayCalculatedState = {
    day,
    durationInHours,
    firstDuration,
    isDayChecked,
    isDurationSelected,
    isOvernight,
    knownDurationInHours,
    minStartTime,
    mustChooseTimeslotInFuture,
    showTimesLabel,
    startsToday,
    isValidRange,
    isSelectedTimeInAvailabilityRange,
    startDate,
    endDate,
  };

  return state;
}

function addHoursAndRoundUp(params: {
  time: LocalTime;
  hoursToAdd: number;
  roundingMinutes: number;
}) {
  // Add the specified number of hours to the provided LocalTime
  let time = params.time.plusHours(params.hoursToAdd);

  const minutes = time.minute();

  // Calculate the next rounding point
  const nextRoundingPoint = Math.ceil(minutes / params.roundingMinutes) * params.roundingMinutes;

  // Adjust the time based on the next rounding point
  if (nextRoundingPoint < 60) {
    time = time.withMinute(nextRoundingPoint);
  } else {
    time = time.plusHours(1).withMinute(0);
  }

  return time.withSecond(0).withNano(0);
}

export function getIsFlexibleDaysVisit(
  broadcastDetails: PublicVisitBroadcastDetails | PublicOnboardingVisitDetails
) {
  if (broadcastDetails.type !== "FlexiblePattern") {
    return false;
  }

  switch (broadcastDetails.flexibleVisitParams.type) {
    case "PermanentFixedHoursFlexibleDays":
    case "PermanentFlexibleHoursAndDaysPerWeek":
    case "PermanentFlexibleHoursAndDaysPattern":
      return true;

    case "PermanentFlexibleHoursFixedDaysPattern":
    case "PermanentFlexibleHoursFixedDaysPerWeek":
    case "SingleFlexible":
      return false;
  }
}

export function getShowSelectAllCheckbox(
  broadcastDetails: PublicVisitBroadcastDetails | PublicOnboardingVisitDetails
) {
  if (
    broadcastDetails.type === "FlexiblePattern" &&
    broadcastDetails.flexibleVisitParams.type === "SingleFlexible"
  ) {
    return false;
  }

  return !getIsFlexibleDaysVisit(broadcastDetails);
}
